import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { customerApi, invoiceApi, companyApi, productApi, companySettingsApi, type Customer, type InvoiceItem, type Product } from '../../lib/api';
import { useCompany } from '../CompanyProvider';
import { useToast } from '../ToastProvider';
import { Button, Card, ErrorNote, Field, HelpBadge, PageHeader, euro, displayCustomer } from '../ui';

interface DraftItem {
  description: string;
  quantity: string;
  unitPrice: string; // Euro als Text-Eingabe
  discount: string;  // Rabatt-% als Text
  taxRate: string;
  productId?: string | null;
}

const emptyItem: DraftItem = { description: '', quantity: '1', unitPrice: '', discount: '', taxRate: '19' };

// Kleinbetragrechnung (§33 UStDV): vereinfachte Form bis 250 € brutto.
const KLEINBETRAG_LIMIT_CENTS = 250_00;

function toCents(euroStr: string): number {
  const n = parseFloat(euroStr.replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function custLabel(c: Customer): string {
  const num = c.customerNumber ? `${c.customerNumber} · ` : '';
  return `${num}${displayCustomer(c)}`;
}

/**
 * Datumsfeld, das eine UNVOLLSTAENDIGE Eingabe erkennt.
 *
 * Problem: <input type="date"> liefert einen LEEREN String, solange das Datum
 * nicht vollstaendig ist (z.B. "04.10.202" waehrend des Tippens). Fuer das
 * Formular sah das aus wie "nichts eingegeben" — und das Backend setzte seinen
 * Default "heute". Wer eine Rechnung auf 2022 datieren wollte, bekam
 * stillschweigend das heutige Datum und damit auch eine Nummer aus dem
 * falschen Jahr.
 *
 * Loesung: Wir beobachten zusaetzlich die Gueltigkeit des Feldes
 * (validity.badInput = etwas eingetippt, aber unvollstaendig/ungueltig) und
 * melden das nach oben, damit das Speichern blockiert werden kann.
 */
function DateField({
  label,
  value,
  onChange,
  onIncompleteChange,
  incomplete,
  hint,
  shakeKey = 0,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onIncompleteChange?: (bad: boolean) => void;
  incomplete?: boolean;
  hint?: string;
  /** Hochzaehlen laesst das Feld erneut ruetteln (Fehlversuch beim Speichern). */
  shakeKey?: number;
}): ReactElement {
  const cls =
    'w-full rounded-xl border px-4 py-2.5 text-base outline-none transition focus:ring-2 ' +
    (incomplete
      ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200');
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {/* key erzwingt beim Hochzaehlen einen Remount — nur so startet die
          CSS-Animation bei einem erneuten Fehlversuch wirklich neu. */}
      <div key={shakeKey} className={incomplete && shakeKey > 0 ? 'animate-shake' : undefined}>
        <input
          type="date"
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            // badInput: Es steht etwas im Feld, ergibt aber (noch) kein Datum.
            onIncompleteChange?.(e.target.validity.badInput);
          }}
          onBlur={(e) => onIncompleteChange?.(e.target.validity.badInput)}
          className={cls}
        />
      </div>
      {incomplete ? (
        <p className="mt-1 text-xs text-red-500">
          Das Datum ist unvollständig. Bitte vierstellige Jahreszahl eingeben (z.&nbsp;B. 04.10.2022).
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-gray-500">{hint}</p>
      ) : null}
    </label>
  );
}

export default function NewInvoice(): ReactElement {
  const { active } = useCompany();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Bearbeiten-Modus: Route /rechnungen/:id/bearbeiten (nur Entwuerfe).
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  // Im Edit-Modus bestimmt der geladene Entwurf den Typ, nicht die Query.
  const [editIsQuote, setEditIsQuote] = useState(false);
  const isQuote = isEdit ? editIsQuote : searchParams.get('typ') === 'angebot';
  const docWord = isQuote ? 'Angebot' : 'Rechnung';
  const [loadingDraft, setLoadingDraft] = useState(false);
  // Angebote-Feature ab Plus; Gate aus den Firmen-Features.
  const [features, setFeatures] = useState<string[] | null>(null);
  const quoteLocked = isQuote && features !== null && !features.includes('angebote');
  const [validUntil, setValidUntil] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [kleinunternehmer, setKleinunternehmer] = useState(false);
  const [kuReason, setKuReason] = useState<string | null>(null);

  // Empfänger: entweder ein ausgewählter Kunde ODER manuell.
  const [selected, setSelected] = useState<Customer | null>(null);
  const [query, setQuery] = useState(''); // Suchtext im Empfänger-Feld
  const [showSuggest, setShowSuggest] = useState(false);
  const [recipientAddress, setRecipientAddress] = useState('');

  const [kleinbetrag, setKleinbetrag] = useState(false);
  const [issueDate, setIssueDate] = useState(''); // leer = heute (Backend-Default); gesetzt = rueckwirkend
  const [dueDate, setDueDate] = useState('');
  // Unvollstaendige Datumseingabe (z.B. Jahr noch nicht fertig getippt).
  const [dateIncomplete, setDateIncomplete] = useState(false);
  const [dueIncomplete, setDueIncomplete] = useState(false);
  const [validUntilIncomplete, setValidUntilIncomplete] = useState(false);
  // Jahr < 1000 = zweistellig getippt ("17.12.23" -> Jahr 0023). Das Feld ist
  // dann technisch gueltig, das Datum aber falsch — und es wuerde den
  // Nummernkreis verschieben (RE-0023-0001 statt RE-2023-0164).
  const implausibleYear = (v: string) => {
    if (!v) return false;
    const y = Number(v.slice(0, 4));
    return Number.isFinite(y) && y < 1000;
  };
  const badYear = implausibleYear(issueDate) || implausibleYear(dueDate) || implausibleYear(validUntil);

  // Hochzaehlen loest das Ruetteln aus (jeder Fehlversuch erneut).
  const [shakeTick, setShakeTick] = useState(0);
  // Skonto (ab Professional). Default aus Firma, je Rechnung aenderbar.
  const skontoAvailable = features !== null && features.includes('skonto');
  const [skontoPercent, setSkontoPercent] = useState('');
  const [skontoDays, setSkontoDays] = useState('');
  const [items, setItems] = useState<DraftItem[]>([{ ...emptyItem }]);
  const [products, setProducts] = useState<Product[]>([]);
  const [pickerIdx, setPickerIdx] = useState<number | null>(null); // offenes Produkt-Dropdown je Position
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) return;
    customerApi.list(active.id).then(setCustomers).catch(() => undefined);
    companyApi.usage(active.id).then((u) => setFeatures(u.features)).catch(() => setFeatures([]));
    productApi.list(active.id).then(setProducts).catch(() => undefined); // 402 ohne Feature -> leer
    // Skonto-Defaults der Firma vorbelegen — im Edit-Modus NICHT, dort gelten
    // die Werte des Entwurfs.
    if (!editId) {
      companySettingsApi.get(active.id).then((c) => {
        const p = Number(c.skontoPercent ?? 0);
        if (p > 0) { setSkontoPercent(String(p)); if (c.skontoDays != null) setSkontoDays(String(c.skontoDays)); }
      }).catch(() => undefined);
    }
    // Kleinunternehmer-Status JAHRESABHAENGIG (§19 UStG): nach aktuellem Jahr,
    // weil der Status beim Ueberschreiten der Umsatzgrenzen wechselt.
    const year = new Date().getFullYear();
    companyApi
      .kuStatus(active.id, year)
      .then((s) => {
        setKuReason(s.reason);
        if (s.isKleinunternehmer) {
          setKleinunternehmer(true);
          // Steuersaetze nur bei Neuanlage ueberschreiben — beim Bearbeiten
          // stammen sie aus dem Entwurf.
          if (!editId) setItems((prev) => prev.map((it) => ({ ...it, taxRate: '0' })));
        }
      })
      .catch(() => undefined);
  }, [active, editId]);

  // --- Entwurf laden (Bearbeiten-Modus) ---
  // Cents/Decimal zurueck in die Text-Felder des Formulars uebersetzen.
  useEffect(() => {
    if (!active || !editId) return;
    setLoadingDraft(true);
    invoiceApi.get(active.id, editId)
      .then((inv) => {
        setEditIsQuote(inv.type === 'quote');
        setItems(
          (inv.items ?? []).map((it) => ({
            description: it.description,
            quantity: String(Number(it.quantity)).replace('.', ','),
            unitPrice: (it.unitPriceCents / 100).toFixed(2).replace('.', ','),
            discount: Number(it.discountPercent ?? 0) > 0 ? String(Number(it.discountPercent)).replace('.', ',') : '',
            taxRate: String(Number(it.taxRate)),
            productId: it.productId ?? null,
          })),
        );
        if (inv.issueDate) setIssueDate(inv.issueDate.slice(0, 10));
        if (inv.dueDate) setDueDate(inv.dueDate.slice(0, 10));
        if (inv.validUntil) setValidUntil(inv.validUntil.slice(0, 10));
        const sp = Number(inv.skontoPercent ?? 0);
        setSkontoPercent(sp > 0 ? String(sp) : '');
        setSkontoDays(inv.skontoDays != null ? String(inv.skontoDays) : '');
        setRecipientAddress(inv.recipientAddress ?? '');
        setQuery(inv.recipientName ?? '');
        // KU-Status aus der gespeicherten Rechnung ableiten (taxCents === 0),
        // nicht aus dem heutigen Firmenstatus — sonst kippen Altbelege.
        if (inv.taxCents === 0) setKleinunternehmer(true);
      })
      .catch(setError)
      .finally(() => setLoadingDraft(false));
  }, [active, editId]);

  // Im Edit-Modus den gespeicherten Kunden im Autocomplete rekonstruieren.
  useEffect(() => {
    if (!isEdit || !editId || !active || customers.length === 0 || selected) return;
    invoiceApi.get(active.id, editId)
      .then((inv) => {
        if (!inv.customerId) return;
        const c = customers.find((x) => x.id === inv.customerId);
        if (c) { setSelected(c); setQuery(custLabel(c)); }
      })
      .catch(() => undefined);
    // selected bewusst nicht in deps: nur einmal vorbelegen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId, active, customers]);

  // Klick außerhalb schließt die Vorschlagsliste.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setShowSuggest(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Vorschläge: Name/Firma/Nummer enthält den Suchtext.
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers.slice(0, 8);
    return customers
      .filter((c) => `${displayCustomer(c)} ${c.customerNumber ?? ''} ${c.email ?? ''}`.toLowerCase().includes(q))
      .slice(0, 8);
  }, [customers, query]);

  // Live-Summe (gleiche Logik wie Server: pro Position runden).
  // Kleinunternehmer (§19 UStG): keine USt.
  let net = 0;
  let tax = 0;
  for (const it of items) {
    const gross = (parseFloat(it.quantity.replace(',', '.')) || 0) * toCents(it.unitPrice);
    const disc = (parseFloat(it.discount.replace(',', '.')) || 0) / 100;
    const lineNet = Math.round(gross * (1 - disc));
    net += lineNet;
    if (!kleinunternehmer) tax += Math.round((lineNet * (parseFloat(it.taxRate) || 0)) / 100);
  }
  const gross = net + tax;
  const overKleinbetrag = kleinbetrag && gross > KLEINBETRAG_LIMIT_CENTS;

  function pickCustomer(c: Customer) {
    setSelected(c);
    setQuery(custLabel(c));
    setShowSuggest(false);
    // Adresse aus Stammdaten vorbefüllen (nur Anzeige; Server snapshottet selbst).
    setRecipientAddress([c.street, `${c.postalCode ?? ''} ${c.city ?? ''}`.trim()].filter(Boolean).join('\n'));
    // Kunden-Standardrabatt auf Positionen ohne eigenen Rabatt vorbelegen.
    const cd = Number(c.discountPercent ?? 0);
    if (cd > 0) {
      setItems((prev) => prev.map((it) => (it.discount.trim() ? it : { ...it, discount: String(cd) })));
    }
  }

  function clearCustomer() {
    setSelected(null);
    setQuery('');
    setRecipientAddress('');
  }

  function updateItem(idx: number, patch: Partial<DraftItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  // Produkt in eine Position uebernehmen (Beschreibung, Preis, USt).
  function pickProduct(idx: number, p: Product) {
    updateItem(idx, {
      description: p.description?.trim() || p.name,
      unitPrice: (p.unitPriceCents / 100).toFixed(2).replace('.', ','),
      taxRate: kleinunternehmer ? '0' : String(Number(p.taxRate)),
      productId: p.id,
    });
    setPickerIdx(null);
  }

  async function save() {
    if (!active) return;
    setError(null);
    const apiItems: InvoiceItem[] = items
      .filter((it) => it.description.trim())
      .map((it) => ({
        description: it.description.trim(),
        quantity: parseFloat(it.quantity.replace(',', '.')) || 1,
        unitPriceCents: toCents(it.unitPrice),
        discountPercent: parseFloat(it.discount.replace(',', '.')) || 0,
        taxRate: parseFloat(it.taxRate) || 0,
        productId: it.productId ?? null,
      }));
    if (apiItems.length === 0) {
      setError(new Error('Bitte mindestens eine Position mit Beschreibung angeben.'));
      return;
    }
    // Unvollstaendiges Datum NICHT stillschweigend als leer durchlassen — das
    // Backend wuerde sonst heute einsetzen und die Rechnung bekaeme ein
    // falsches Datum samt Nummer aus dem falschen Jahr.
    if (dateIncomplete || dueIncomplete || validUntilIncomplete || badYear) {
      setShakeTick((t) => t + 1);
      setError(new Error('Ein Datum ist nicht vollständig. Bitte die vierstellige Jahreszahl eingeben (z. B. 17.12.2023).'));
      return;
    }

    // Manuell eingetippter Empfänger ohne Kundenauswahl.
    const manualName = !selected && query.trim() ? query.trim() : undefined;
    setBusy(true);
    const payload = {
        type: (isQuote ? 'quote' : 'invoice') as 'quote' | 'invoice',
        customerId: selected?.id || undefined,
        recipientName: manualName,
        recipientAddress: !selected && recipientAddress.trim() ? recipientAddress.trim() : undefined,
        // Bei manuellem Empfaenger automatisch Kunde anlegen — ausser bei
        // Kleinbetragrechnung (Barverkauf/Quittung, oft anonym).
        autoCreateCustomer: !kleinbetrag,
        // Rechnungsdatum: leer -> Backend nimmt heute; gesetzt -> rueckwirkend
        // (bestimmt auch Rechnungsnummer-Jahr + KU-Status des Jahres).
        issueDate: !isQuote && issueDate ? issueDate : undefined,
        dueDate: !isQuote && dueDate ? dueDate : undefined,
        validUntil: isQuote && validUntil ? validUntil : undefined,
        skontoPercent: !isQuote && skontoAvailable && skontoPercent.trim() ? (parseFloat(skontoPercent.replace(',', '.')) || 0) : undefined,
        skontoDays: !isQuote && skontoAvailable && skontoDays.trim() ? (parseInt(skontoDays, 10) || 0) : undefined,
        items: apiItems,
    };
    try {
      const inv = isEdit && editId
        ? await invoiceApi.update(active.id, editId, payload)
        : await invoiceApi.create(active.id, payload);
      toast.success(
        isEdit
          ? `${docWord} ${inv.number ?? ''} aktualisiert.`
          : `${docWord} ${inv.number ?? ''} erstellt.${!selected && manualName && !kleinbetrag ? ' Kunde automatisch angelegt.' : ''}`,
      );
      navigate(`/app/rechnungen/${inv.id}`, { replace: true });
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={isEdit ? `${docWord} bearbeiten` : (isQuote ? 'Neues Angebot' : 'Neue Rechnung')} subtitle={isEdit ? 'Entwurf ändern – Nummer und Datum bleiben erhalten.' : 'Fülle die Positionen aus – die Summe rechnen wir für dich.'} />

      {quoteLocked && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
          🔒 Angebote sind ab dem <strong>Plus</strong>-Tarif verfügbar.{' '}
          <a href="/app/einstellungen/tarif" className="font-medium underline">Tarif ansehen</a>
        </div>
      )}

      <Card className="space-y-5">
        <ErrorNote error={error} />

        {kleinunternehmer && (
          <div className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            <strong>Kleinunternehmer (§19 UStG)</strong> für {new Date().getFullYear()} – ohne USt-Ausweis.
            {kuReason ? <span className="block text-amber-700 dark:text-amber-400/80">{kuReason}</span> : null}
          </div>
        )}

        {/* Kleinbetragrechnung-Schalter (nur fuer Rechnungen, nicht fuer Angebote) */}
        {!isQuote && (
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
          <input
            type="checkbox"
            checked={kleinbetrag}
            onChange={(e) => setKleinbetrag(e.target.checked)}
            className="mt-0.5 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm">
            <span className="flex items-center gap-1 font-medium text-gray-900">
              Kleinbetragrechnung (Kurzform)
              <HelpBadge text="Für Rechnungen bis 250 € brutto (z. B. Quittungen, Barverkäufe). Empfängerangaben sind nicht zwingend nötig – nur Datum, Leistung, Betrag und Steuersatz. (§ 33 UStDV)" />
            </span>
            <span className="text-gray-500">Vereinfacht – ohne Empfängeradresse, bis 250 € brutto.</span>
          </span>
        </label>
        )}

        {/* Empfänger: Autocomplete über bestehende Kunden + manuell */}
        <div ref={boxRef} className="relative">
          <span className="mb-1.5 block text-sm font-medium text-gray-700">
            {kleinbetrag ? 'Empfänger (optional)' : 'Empfänger / Kunde'}
          </span>
          <div className="relative">
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                if (selected) setSelected(null); // bei Tippen Auswahl lösen
                setShowSuggest(true);
              }}
              onFocus={() => setShowSuggest(true)}
              placeholder="Name tippen – bestehende Kunden erscheinen automatisch …"
              className="w-full rounded-xl border border-gray-300 px-4 py-2.5 pr-10 text-base outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            />
            {selected && (
              <button
                type="button"
                onClick={clearCustomer}
                aria-label="Auswahl entfernen"
                className="absolute inset-y-0 right-2 my-auto flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100"
              >
                ✕
              </button>
            )}
          </div>

          {/* Vorschlagsliste */}
          {showSuggest && suggestions.length > 0 && !selected && (
            <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
              {suggestions.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => pickCustomer(c)}
                    className="flex w-full flex-col items-start px-4 py-2 text-left hover:bg-blue-50"
                  >
                    <span className="text-sm font-medium text-gray-900">{displayCustomer(c)}</span>
                    <span className="text-xs text-gray-500">
                      {c.customerNumber ? `${c.customerNumber}` : 'Kunde'}
                      {c.city ? ` · ${c.city}` : ''}
                      {c.email ? ` · ${c.email}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {selected ? (
            <p className="mt-1.5 text-xs text-green-700">
              ✓ Kunde {selected.customerNumber ? `${selected.customerNumber} ` : ''}übernommen – Adresse & Nummer erscheinen automatisch auf der Rechnung.
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-gray-500">
              {kleinbetrag
                ? 'Kein passender Kunde? Bei der Kleinbetragrechnung wird kein Kunde gespeichert.'
                : 'Kein passender Kunde? Der Empfänger wird automatisch als neuer Kunde angelegt – mit eigener Kundennummer.'}
            </p>
          )}

          {/* Manuelle Adresse nur, wenn kein Kunde gewählt und keine Kurzform */}
          {!selected && !kleinbetrag && (
            <label className="mt-3 block">
              <span className="mb-1.5 block text-sm font-medium text-gray-700">Adresse</span>
              <textarea
                value={recipientAddress}
                onChange={(e) => setRecipientAddress(e.target.value)}
                placeholder="Straße, PLZ Ort"
                rows={2}
                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-base outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              />
            </label>
          )}
        </div>

        {isQuote ? (
          <DateField label="Gültig bis" value={validUntil} onChange={setValidUntil}
            incomplete={validUntilIncomplete || implausibleYear(validUntil)} onIncompleteChange={setValidUntilIncomplete} shakeKey={shakeTick} />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <DateField label="Rechnungsdatum" value={issueDate} onChange={setIssueDate}
              incomplete={dateIncomplete || implausibleYear(issueDate)} onIncompleteChange={setDateIncomplete} shakeKey={shakeTick}
              hint="Leer = heute. Für rückwirkende Rechnungen ein früheres Datum wählen." />
            <DateField label="Fällig am" value={dueDate} onChange={setDueDate}
              incomplete={dueIncomplete || implausibleYear(dueDate)} onIncompleteChange={setDueIncomplete} shakeKey={shakeTick} />
          </div>
        )}

        {!isQuote && skontoAvailable && (
          <div className="rounded-xl border border-gray-200 p-3">
            <div className="mb-2 text-sm font-medium text-gray-700">Skonto (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Skonto %" value={skontoPercent} onChange={setSkontoPercent} placeholder="z. B. 2" />
              <Field label="innerhalb Tagen" value={skontoDays} onChange={setSkontoDays} placeholder="z. B. 14" />
            </div>
            {(parseFloat(skontoPercent.replace(',', '.')) || 0) > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                Bei Zahlung innerhalb der Frist: {euro(Math.round(gross * (1 - (parseFloat(skontoPercent.replace(',', '.')) || 0) / 100)))} statt {euro(gross)}.
              </p>
            )}
          </div>
        )}

        <div>
          <div className="mb-2 text-sm font-medium text-gray-700">Positionen</div>
          <div className="space-y-3">
            {items.map((it, idx) => (
              <div key={idx} className="rounded-xl border border-gray-200 p-3">
                {products.length > 0 && (
                  <div className="relative mb-2">
                    <button
                      type="button"
                      onClick={() => setPickerIdx(pickerIdx === idx ? null : idx)}
                      className="text-xs font-medium text-blue-600 hover:underline"
                    >
                      + Produkt einfügen
                    </button>
                    {pickerIdx === idx && (
                      <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg">
                        {products.map((p) => (
                          <button
                            key={p.id}
                            type="button"
                            onClick={() => pickProduct(idx, p)}
                            className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-blue-50"
                          >
                            <span className="min-w-0 truncate">
                              {p.name}
                              {p.sku ? <span className="ml-1 text-xs text-gray-400">{p.sku}</span> : null}
                            </span>
                            <span className="shrink-0 text-xs text-gray-500">{euro(p.unitPriceCents)} · {Number(p.taxRate)}%</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <input
                  value={it.description}
                  onChange={(e) => updateItem(idx, { description: e.target.value })}
                  placeholder="Beschreibung (z. B. Beratung)"
                  className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
                <div className="grid grid-cols-4 gap-2">
                  <label className="text-xs text-gray-500">
                    Menge
                    <input
                      value={it.quantity}
                      onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs text-gray-500">
                    Einzelpreis €
                    <input
                      value={it.unitPrice}
                      onChange={(e) => updateItem(idx, { unitPrice: e.target.value })}
                      placeholder="0,00"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs text-gray-500">
                    Rabatt %
                    <input
                      value={it.discount}
                      onChange={(e) => updateItem(idx, { discount: e.target.value })}
                      placeholder="0"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
                    />
                  </label>
                  <label className="text-xs text-gray-500">
                    USt %
                    <select
                      value={kleinunternehmer ? '0' : it.taxRate}
                      onChange={(e) => updateItem(idx, { taxRate: e.target.value })}
                      disabled={kleinunternehmer}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
                    >
                      {kleinunternehmer ? (
                        <option value="0">0% (Kleinunternehmer)</option>
                      ) : (
                        <>
                          <option value="19">19%</option>
                          <option value="7">7%</option>
                          <option value="0">0%</option>
                        </>
                      )}
                    </select>
                  </label>
                </div>
                {items.length > 1 && (
                  <button
                    onClick={() => setItems((p) => p.filter((_, i) => i !== idx))}
                    className="mt-2 text-xs text-red-600 hover:underline"
                  >
                    Position entfernen
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            onClick={() => setItems((p) => [...p, { ...emptyItem, taxRate: kleinunternehmer ? '0' : emptyItem.taxRate }])}
            className="mt-3 text-sm font-medium text-blue-600 hover:underline"
          >
            + Position hinzufügen
          </button>
        </div>

        <div className="space-y-1 border-t border-gray-100 pt-4 text-right text-sm">
          {kleinunternehmer ? (
            <>
              <div className="text-lg font-bold text-gray-900">Gesamt: {euro(gross)}</div>
              <div className="text-xs text-gray-500">Kein USt-Ausweis gemäß § 19 UStG (Kleinunternehmer).</div>
            </>
          ) : (
            <>
              <div className="text-gray-600">Netto: {euro(net)}</div>
              <div className="text-gray-600">USt: {euro(tax)}</div>
              <div className="text-lg font-bold text-gray-900">Gesamt: {euro(gross)}</div>
            </>
          )}
        </div>

        {overKleinbetrag && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            Hinweis: Über 250 € brutto ist keine Kleinbetragrechnung mehr zulässig – bitte den Empfänger vollständig angeben.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => navigate(isEdit && editId ? `/app/rechnungen/${editId}` : "/app/rechnungen")}>
            Abbrechen
          </Button>
          <Button onClick={save} busy={busy || loadingDraft} disabled={overKleinbetrag || quoteLocked || loadingDraft}>
            {isEdit ? 'Änderungen speichern' : (isQuote ? 'Angebot erstellen' : 'Rechnung erstellen')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
